import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  AppKey,
  AppPrice,
  AppPricingService,
  FieldMask,
  GetMultiAppRequest,
  PartnerApiService,
} from '@galaxy/marketplace-apps';
import { EditionSelectorService, HeaderActionType, ResponseType } from '@vendasta/store';
import { Breadcrumbs } from '@vendasta/uikit';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { InIFrame } from '../common';
import { buildStoreRedirectURLForApp } from '../util/store';

@Component({
  selector: 'strfrnt-view',
  template: `
    @if (params$ | async; as params) {
      <va-breadcrumbs [breadcrumbs]="breadcrumbs$ | async" class="breadcrumbs"></va-breadcrumbs>
      @if (!params.addonId) {
        <store-marketplace-product
          [productId]="params.appId"
          [partnerId]="params.partnerId"
          [appPrice]="appPrice$ | async"
          [currency]="currency$ | async"
          [marketId]="params.marketId"
          [headerActionType]="ActionType"
          [hideTags]="true"
          (headerActionClick)="handleAppActionEvent(params.partnerId, params.marketId, params.appId)"
          (editionActionClick)="
            handleEditionActionEvent(params.partnerId, params.marketId, $event.appId, $event.editionId)
          "
          (addonClick)="navigateToAddon($event)"
        ></store-marketplace-product>
      } @else {
        <store-marketplace-product
          [productId]="params.addonId"
          [partnerId]="params.partnerId"
          [appPrice]="appPrice$ | async"
          [currency]="currency$ | async"
          [marketId]="params.marketId"
          [headerActionType]="ActionType"
          [hideTags]="true"
          (headerActionClick)="handleAddonActionEvent(params.partnerId, params.marketId, params.appId, params.addonId)"
        ></store-marketplace-product>
      }
    }
  `,
  styleUrls: ['./app-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppViewComponent implements OnInit {
  params$: Observable<Params>;
  ActionType = HeaderActionType.BUY_IT_NOW;

  partnerId$: Observable<string>;
  marketId$: Observable<string>;
  appId$: Observable<string>;
  addonId$: Observable<string>;
  appPrice$: Observable<AppPrice>;
  currency$: Observable<string>;

  public breadcrumbs$: Observable<Breadcrumbs[]>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private editionSelectorService: EditionSelectorService,
    private partnerApiService: PartnerApiService,
    private appPricingService: AppPricingService,
  ) {}

  ngOnInit(): void {
    this.params$ = this.route.params.pipe(filter(Boolean));
    this.partnerId$ = this.params$.pipe(map((paramMap) => paramMap.partnerId));
    this.marketId$ = this.params$.pipe(map((paramMap) => paramMap.marketId));
    this.appId$ = this.params$.pipe(map((paramMap) => paramMap.appId));
    this.addonId$ = this.params$.pipe(map((paramMap) => paramMap.addonId));

    const productIds$ = combineLatest([this.appId$, this.addonId$]).pipe(map((ids) => ids.filter(Boolean)));

    const products$ = combineLatest([this.partnerId$, this.marketId$, productIds$]).pipe(
      switchMap(([partnerId, marketId, productIds]) =>
        this.partnerApiService.getMultiApp(
          new GetMultiAppRequest({
            partnerId,
            marketId,
            appKeys: productIds.map((id) => new AppKey({ appId: id })),
          }),
        ),
      ),
      map((resp) => resp.apps),
    );

    this.appPrice$ = combineLatest([this.partnerId$, this.marketId$, this.params$]).pipe(
      switchMap(([partnerId, marketId, params]) => {
        return this.appPricingService.getMultiPricing(
          [params.addonId || params.appId],
          new FieldMask({ paths: ['retail'] }),
          partnerId,
          marketId,
        );
      }),
      map((appPrices) => {
        if (appPrices) {
          return appPrices[0].pricesForContexts['retail'];
        }
      }),
      shareReplay(1),
    );

    this.currency$ = this.appPrice$.pipe(
      map((data) => {
        const firstedition = Object.keys(data?.pricesForEditions)[0];
        if (data?.pricesForEditions?.[firstedition]?.pricesForCurrencies !== undefined) {
          return Object.keys(data?.pricesForEditions?.[firstedition]?.pricesForCurrencies)[0];
        }
      }),
    );

    this.breadcrumbs$ = combineLatest([this.marketId$, this.partnerId$, products$]).pipe(
      map(([marketId, partnerId, products]) => {
        const breadcrumbs = [{ text: 'Store', url: this.buildStoreUrl(partnerId, marketId) }];
        if (products && products.length > 1) {
          breadcrumbs.push({
            text: products[0]?.sharedMarketingInformation.name,
            url: this.buildStoreUrl(partnerId, marketId, products[0].key.appId),
          });
          breadcrumbs.push({ text: products[1]?.sharedMarketingInformation.name, url: null });
        } else if (products && products.length == 1) {
          breadcrumbs.push({ text: products[0]?.sharedMarketingInformation.name, url: null });
        }
        return breadcrumbs;
      }),
    );
  }

  buildStoreUrl(partnerId: string, marketId: string, appId?: string, addonId?: string): string {
    let url = `/public/store/${partnerId}/${marketId || ''}`;
    if (appId) {
      url += `/app/${appId}`;
      if (addonId) {
        url += `/addon/${addonId}`;
      }
    }
    return url;
  }

  handleAppActionEvent(partnerId: string, marketId: string, appId: string): void {
    this.editionSelectorService
      .openEditionSelector({
        partnerId,
        marketId,
        productId: appId,
        translationKeys: {
          title: 'FRONTEND.STORE.EDITION_SELECTOR.ADD_APP_SELECTION.TITLE',
        },
        appURL: this.buildStoreUrl(partnerId, marketId, appId),
        appPrice$: this.appPrice$,
        currency$: this.currency$,
      })
      .pipe(
        take(1),
        filter((resp) => resp && [ResponseType.EditionsUnsupported, ResponseType.EditionSelected].includes(resp.type)),
      )
      .subscribe((resp) => {
        this.redirectToCartReview(partnerId, marketId, appId, resp.editionId);
      });
  }

  handleEditionActionEvent(partnerId: string, marketId: string, appId: string, editionId: string): void {
    this.redirectToCartReview(partnerId, marketId, appId, editionId);
  }

  handleAddonActionEvent(partnerId: string, marketId: string, appId: string, addonId: string): void {
    this.redirectToCartReview(partnerId, marketId, appId, null, addonId);
  }

  redirectToCartReview(partnerId: string, marketId: string, appId: string, editionId?: string, addonId?: string): void {
    const redirectPath = buildStoreRedirectURLForApp(partnerId, marketId, appId, editionId, addonId);
    if (InIFrame()) {
      window.open(redirectPath);
    } else {
      window.location.href = redirectPath;
    }
  }

  navigateToAddon(addonId: string): void {
    this.router.navigate([`./addon/${addonId}`], { relativeTo: this.route });
  }
}
